// Mind2Magic CRM — PDF Finalizer (Magic Planner 1.5)
// Converts Sara's edited DOCX (or generated data) into a branded Mind2Magic PDF.
// Uses pdf-lib + fontkit for custom fonts, lazy-loaded in browser.
//
// Two modes:
//   1. HTML mode — parse edited DOCX via mammoth → HTML → PDF
//   2. Direct mode — generate PDF from lead data + content-blocks.js (no DOCX needed)

import { store } from '../store.js'
import {
  extractFamilyName, getParkName, getFirstHotel, detectParks,
  getTravelStartDate, getEnhancedHours, formatDateShort,
  isClosedAttraction, detectNoCoasters, filterRides, filterShows,
  buildDefaultSchedule, detectMealPlanType, downloadBlob,
} from './itinerary-helpers.js'
import {
  classifyHotel, SECTION_MARKERS, GREETING,
  PARK_RIDES, PARK_SHOWS, PARK_CHARACTERS, PARK_RESTAURANTS,
  PARK_ENTERTAINMENT,
  LIGHTNING_LANE, DISNEY_SPRINGS, CITY_WALK, MEAL_PLAN,
  INFO_ADICIONAL, SHOPPING, CHECK_IN_DAY, EPIC_UNIVERSE,
  UNIVERSAL_PARKS, SKYLINER_HOTELS, SKYLINER_EPCOT_TIP,
} from './content-blocks.js'

// ── PAGE LAYOUT CONSTANTS ────────────────────────────────────────────────

const PAGE_W = 595.28 // A4
const PAGE_H = 841.89
const MARGIN_L = 55
const MARGIN_R = 55
const MARGIN_TOP = 70
const MARGIN_BOTTOM = 60
const CONTENT_W = PAGE_W - MARGIN_L - MARGIN_R

// ── BRAND COLORS ─────────────────────────────────────────────────────────

let rgb // assigned after lazy-load

function initColors(_rgb) {
  rgb = _rgb
}

const c = {
  get PURPLE() { return rgb(107 / 255, 78 / 255, 255 / 255) },
  get DARK() { return rgb(30 / 255, 30 / 255, 40 / 255) },
  get GRAY() { return rgb(100 / 255, 100 / 255, 110 / 255) },
  get WHITE() { return rgb(1, 1, 1) },
  get LIGHT_BG() { return rgb(245 / 255, 243 / 255, 250 / 255) },
  get LIGHT_RED() { return rgb(255 / 255, 240 / 255, 240 / 255) },
}

const PARK_COLORS = {
  get mk() { return rgb(0 / 255, 90 / 255, 180 / 255) },
  get ak() { return rgb(34 / 255, 120 / 255, 50 / 255) },
  get ep() { return rgb(100 / 255, 50 / 255, 160 / 255) },
  get hs() { return rgb(180 / 255, 30 / 255, 30 / 255) },
  get uso() { return rgb(20 / 255, 100 / 255, 180 / 255) },
  get ioa() { return rgb(0 / 255, 130 / 255, 130 / 255) },
  get epic() { return rgb(200 / 255, 100 / 255, 0 / 255) },
}

const PARK_LIGHT = {
  get mk() { return rgb(230 / 255, 240 / 255, 255 / 255) },
  get ak() { return rgb(230 / 255, 250 / 255, 235 / 255) },
  get ep() { return rgb(240 / 255, 230 / 255, 255 / 255) },
  get hs() { return rgb(255 / 255, 235 / 255, 235 / 255) },
  get uso() { return rgb(230 / 255, 240 / 255, 255 / 255) },
  get ioa() { return rgb(225 / 255, 245 / 255, 245 / 255) },
  get epic() { return rgb(255 / 255, 240 / 255, 225 / 255) },
}

const PARK_TEXT = {
  get mk() { return rgb(0, 0.3, 0.7) },
  get ak() { return rgb(0, 0.4, 0.15) },
  get ep() { return rgb(0.3, 0.1, 0.6) },
  get hs() { return rgb(0.6, 0, 0) },
  get uso() { return rgb(0, 0.3, 0.7) },
  get ioa() { return rgb(0, 0.4, 0.4) },
  get epic() { return rgb(0.6, 0.3, 0) },
}

// ── EMOJI STRIP ──────────────────────────────────────────────────────────

function stripEmoji(text) {
  if (!text) return ''
  // Remove emoji characters (surrogate pairs + known emoji ranges)
  return text
    .replace(/[\u{1F300}-\u{1FFFF}]/gu, '')
    .replace(/[\u{2600}-\u{27BF}]/gu, '')
    .replace(/[\u{FE00}-\u{FE0F}]/gu, '')
    .replace(/[\u{200D}]/gu, '')
    .replace(/[\u{20E3}]/gu, '')
    .replace(/[\u{E0020}-\u{E007F}]/gu, '')
    .replace(/[\u{2B00}-\u{2BFF}]/gu, '')   // misc symbols + arrows
    .replace(/[\u{2190}-\u{21FF}]/gu, '')   // arrows
    .replace(/[\u{2300}-\u{23FF}]/gu, '')   // misc technical
    .replace(/[\u{25A0}-\u{25FF}]/gu, '')   // geometric shapes
    .replace(/  +/g, ' ')
    .trim()
}

// ── TEXT WIDTH HELPER ────────────────────────────────────────────────────

function fontWidth(text, font, size) {
  try { return font.widthOfTextAtSize(stripEmoji(text), size) }
  catch { return text.length * size * 0.5 }
}

// ── RENDERER FACTORY ─────────────────────────────────────────────────────

function createRenderer(doc, fonts, logoImage) {
  let page = null
  let y = 0
  let pageNum = 0
  let isCoverPage = false

  const { helvetica, helveticaBold, helveticaOblique, helveticaBoldOblique, titleFont } = fonts

  // ── Page management ──

  function newPage(skipFooter = false) {
    page = doc.addPage([PAGE_W, PAGE_H])
    y = PAGE_H - MARGIN_TOP
    pageNum++
    isCoverPage = skipFooter
    if (!skipFooter) drawFooter()
    return page
  }

  function drawFooter() {
    page.drawLine({
      start: { x: MARGIN_L, y: MARGIN_BOTTOM - 15 },
      end: { x: PAGE_W - MARGIN_R, y: MARGIN_BOTTOM - 15 },
      thickness: 1,
      color: c.PURPLE,
    })
    page.drawText('Mind2Magic Travel \u2014 Sara Vasquez', {
      x: MARGIN_L, y: MARGIN_BOTTOM - 30,
      size: 8, font: helvetica, color: c.GRAY,
    })
    const numStr = `${pageNum}`
    const numW = fontWidth(numStr, helvetica, 8)
    page.drawText(numStr, {
      x: PAGE_W - MARGIN_R - numW, y: MARGIN_BOTTOM - 30,
      size: 8, font: helvetica, color: c.GRAY,
    })
  }

  function ensureSpace(needed) {
    if (y - needed < MARGIN_BOTTOM) newPage()
  }

  // ── Text drawing ──

  function drawText(text, opts = {}) {
    const {
      font = helvetica, size = 10, color = c.DARK,
      indent = 0, maxWidth = CONTENT_W, lineHeight = 1.4,
      align = 'left',
    } = opts
    const clean = stripEmoji(text)
    if (!clean) return

    const words = clean.split(/\s+/)
    let line = ''
    const effectiveWidth = maxWidth - indent

    for (const word of words) {
      const testLine = line ? `${line} ${word}` : word
      const w = fontWidth(testLine, font, size)
      if (w > effectiveWidth && line) {
        ensureSpace(size * lineHeight)
        const lineW = fontWidth(line, font, size)
        const xPos = align === 'center'
          ? MARGIN_L + indent + (effectiveWidth - lineW) / 2
          : MARGIN_L + indent
        page.drawText(line, { x: xPos, y, size, font, color })
        y -= size * lineHeight
        line = word
      } else {
        line = testLine
      }
    }
    if (line) {
      ensureSpace(size * lineHeight)
      const lineW = fontWidth(line, font, size)
      const xPos = align === 'center'
        ? MARGIN_L + indent + (effectiveWidth - lineW) / 2
        : MARGIN_L + indent
      page.drawText(line, { x: xPos, y, size, font, color })
      y -= size * lineHeight
    }
  }

  function drawTextCentered(text, opts = {}) {
    drawText(text, { ...opts, align: 'center' })
  }

  // Draw multi-run text (bold + regular mixed on same line, with wrapping)
  function drawRichText(runs, opts = {}) {
    const { indent = 0, maxWidth = CONTENT_W, lineHeight = 1.4 } = opts
    const effectiveWidth = maxWidth - indent

    // Flatten runs into words with font info
    const wordItems = []
    for (const run of runs) {
      const clean = stripEmoji(run.text)
      if (!clean) continue
      const words = clean.split(/\s+/).filter(Boolean)
      for (const w of words) {
        wordItems.push({ word: w, font: run.font || helvetica, size: run.size || 10, color: run.color || c.DARK })
      }
    }

    let lineItems = []
    let lineWidth = 0

    const flushLine = () => {
      if (!lineItems.length) return
      ensureSpace(lineItems[0].size * lineHeight)
      let x = MARGIN_L + indent
      for (let i = 0; i < lineItems.length; i++) {
        const item = lineItems[i]
        const prefix = i > 0 ? ' ' : ''
        const txt = prefix + item.word
        page.drawText(txt, { x, y, size: item.size, font: item.font, color: item.color })
        x += fontWidth(txt, item.font, item.size)
      }
      y -= lineItems[0].size * lineHeight
      lineItems = []
      lineWidth = 0
    }

    for (const item of wordItems) {
      const spaceW = lineItems.length ? fontWidth(' ', item.font, item.size) : 0
      const wordW = fontWidth(item.word, item.font, item.size)
      if (lineWidth + spaceW + wordW > effectiveWidth && lineItems.length) {
        flushLine()
      }
      lineWidth += spaceW + wordW
      lineItems.push(item)
    }
    flushLine()
  }

  // ── Heading styles ──

  function drawHeading1(text, color = c.PURPLE) {
    // Park day banner — always start new page
    newPage()
    y -= 5
    const clean = stripEmoji(text)
    page.drawRectangle({
      x: MARGIN_L - 10, y: y - 8,
      width: CONTENT_W + 20, height: 32,
      color,
    })
    page.drawText(clean, {
      x: MARGIN_L, y: y - 1,
      size: 18, font: titleFont, color: c.WHITE,
    })
    y -= 40
  }

  function drawHeading2(text) {
    ensureSpace(35)
    y -= 12
    const clean = stripEmoji(text)
    page.drawText(clean, {
      x: MARGIN_L, y,
      size: 14, font: titleFont, color: c.DARK,
    })
    y -= 4
    const textW = fontWidth(clean, titleFont, 14)
    page.drawLine({
      start: { x: MARGIN_L, y },
      end: { x: MARGIN_L + textW + 20, y },
      thickness: 1.5, color: c.PURPLE,
    })
    y -= 14
  }

  function drawHeading3(text) {
    ensureSpace(25)
    y -= 8
    page.drawText(stripEmoji(text), {
      x: MARGIN_L, y,
      size: 11, font: helveticaBold, color: c.DARK,
    })
    y -= 16
  }

  // ── Info / Warning boxes ──

  function drawBox(lines, opts = {}) {
    const { bgColor = c.LIGHT_BG, borderColor = c.PURPLE, textColor = c.DARK, textFont = helveticaBold, textSize = 8, padX = 8, padY = 6 } = opts

    // Pre-calculate height
    const lineCount = lines.reduce((sum, line) => {
      const clean = stripEmoji(line)
      const charW = fontWidth(clean, textFont, textSize)
      return sum + Math.max(1, Math.ceil(charW / (CONTENT_W - padX * 2)))
    }, 0)
    const boxH = lineCount * (textSize * 1.4) + padY * 2

    ensureSpace(boxH + 10)
    const boxY = y - boxH + padY
    page.drawRectangle({
      x: MARGIN_L, y: boxY,
      width: CONTENT_W, height: boxH,
      color: bgColor, borderColor, borderWidth: 1,
    })
    y -= padY
    for (const line of lines) {
      drawText(line, { font: textFont, size: textSize, color: textColor, indent: padX, maxWidth: CONTENT_W - padX })
    }
    y = boxY - 8
  }

  // ── Tables ──

  function drawTable(rows, opts = {}) {
    const { headerColor = c.PURPLE, colWidths = null, altBg = c.LIGHT_BG, rowHeight = 16 } = opts
    if (!rows.length) return

    const cols = rows[0].length
    const widths = colWidths || rows[0].map(() => CONTENT_W / cols)

    // Pre-calculate total height
    const totalH = rows.length * rowHeight + 8
    ensureSpace(totalH)

    for (let r = 0; r < rows.length; r++) {
      const row = rows[r]
      const isHeader = r === 0
      const rH = rowHeight

      ensureSpace(rH + 4)
      let x = MARGIN_L

      for (let ci = 0; ci < row.length; ci++) {
        const w = widths[ci] || (CONTENT_W / cols)

        // Cell background
        if (isHeader) {
          page.drawRectangle({ x, y: y - 4, width: w, height: rH, color: headerColor })
        } else if (r % 2 === 0) {
          page.drawRectangle({ x, y: y - 4, width: w, height: rH, color: altBg })
        }

        // Cell border
        page.drawRectangle({ x, y: y - 4, width: w, height: rH, borderColor: rgb(0.85, 0.85, 0.85), borderWidth: 0.5 })

        // Cell text — truncate if too long
        let txt = stripEmoji(row[ci] || '')
        const maxCellW = w - 8
        while (fontWidth(txt, helvetica, 8) > maxCellW && txt.length > 3) {
          txt = txt.slice(0, -1)
        }
        page.drawText(txt, {
          x: x + 4, y: y + 1,
          size: 8,
          font: isHeader ? helveticaBold : (ci === 0 ? helveticaBold : helvetica),
          color: isHeader ? c.WHITE : c.DARK,
        })
        x += w
      }
      y -= rH
    }
    y -= 8
  }

  // ── Bullet & Checkbox items ──

  function drawBullet(text, opts = {}) {
    const { size = 9, font = helvetica, color = c.DARK, indent = 10 } = opts
    ensureSpace(size * 1.4)
    drawText(`\u2022 ${text}`, { font, size, color, indent })
  }

  function drawCheckbox(name, comment, opts = {}) {
    const { isBold = false, indent = 5 } = opts
    const displayName = stripEmoji(name)
    const clean = isBold ? `[ ] ${displayName}` : `[ ] ${displayName}`
    const nameFont = isBold ? helveticaBold : helvetica

    ensureSpace(16)
    page.drawText(clean, {
      x: MARGIN_L + indent, y,
      size: 10, font: nameFont, color: c.DARK,
    })

    if (comment) {
      const nameW = fontWidth(clean, nameFont, 10)
      const saraX = MARGIN_L + indent + nameW + 5
      const remaining = CONTENT_W - indent - nameW - 10

      if (remaining > 80) {
        // Fits on same line
        const saraText = stripEmoji(`\u2014 ${comment}`)
        // Might still need to wrap
        const saraW = fontWidth(saraText, helveticaOblique, 9)
        if (saraW <= remaining) {
          page.drawText(saraText, { x: saraX, y, size: 9, font: helveticaOblique, color: c.GRAY })
        } else {
          // Wrap to next line
          y -= 12
          drawText(`\u2014 ${comment}`, { font: helveticaOblique, size: 9, color: c.GRAY, indent: indent + 15 })
          return // drawText already advanced y
        }
      } else {
        y -= 12
        drawText(`\u2014 ${comment}`, { font: helveticaOblique, size: 9, color: c.GRAY, indent: indent + 15 })
        return
      }
    }
    y -= 14
  }

  // ── Cover page ──

  function drawCover(lead, config) {
    newPage(true) // skip footer on cover

    // Logo
    if (logoImage) {
      const dims = logoImage.scale(0.12) // ~154x114 from 1280x951
      page.drawImage(logoImage, {
        x: PAGE_W / 2 - dims.width / 2,
        y: PAGE_H - 60 - dims.height,
        width: dims.width, height: dims.height,
      })
      y = PAGE_H - 60 - dims.height - 25
    } else {
      y = PAGE_H - 120
    }

    // Title
    const familyName = extractFamilyName(lead)
    const parks = config?.parks || detectParks(lead)
    const hasDis = parks.some(p => ['mk', 'hs', 'ep', 'ak'].includes(p))
    const hasUni = parks.some(p => ['uso', 'ioa', 'epic'].includes(p))

    let titleText
    if (hasDis && hasUni) titleText = GREETING.headingCombo
    else if (hasUni) titleText = GREETING.headingUniversal
    else titleText = GREETING.heading
    titleText = titleText.replace('{familyName}', familyName)

    const titleClean = stripEmoji(titleText)
    // Split title if too long
    const titleW = fontWidth(titleClean, titleFont, 18)
    if (titleW > CONTENT_W) {
      const mid = Math.ceil(titleClean.length / 2)
      const spaceIdx = titleClean.indexOf(' ', mid - 10)
      const line1 = titleClean.substring(0, spaceIdx > 0 ? spaceIdx : mid)
      const line2 = titleClean.substring(spaceIdx > 0 ? spaceIdx + 1 : mid)
      const w1 = fontWidth(line1, titleFont, 18)
      page.drawText(line1, { x: PAGE_W / 2 - w1 / 2, y, size: 18, font: titleFont, color: c.PURPLE })
      y -= 24
      const w2 = fontWidth(line2, titleFont, 18)
      page.drawText(line2, { x: PAGE_W / 2 - w2 / 2, y, size: 18, font: titleFont, color: c.PURPLE })
      y -= 25
    } else {
      page.drawText(titleClean, { x: PAGE_W / 2 - titleW / 2, y, size: 18, font: titleFont, color: c.PURPLE })
      y -= 28
    }

    // Subtitle
    const subtitle = 'Itinerario Personalizado'
    const subW = fontWidth(subtitle, helvetica, 11)
    page.drawText(subtitle, { x: PAGE_W / 2 - subW / 2, y, size: 11, font: helvetica, color: c.GRAY })
    y -= 18

    // Purple divider
    page.drawLine({
      start: { x: PAGE_W / 2 - 50, y },
      end: { x: PAGE_W / 2 + 50, y },
      thickness: 1.5, color: c.PURPLE,
    })
    y -= 18

    // Greeting text
    const welcomeLines = GREETING.welcome.split('\n').filter(l => l.trim())
    drawText(welcomeLines[0] || '', { size: 10, color: c.DARK })
    y -= 3
    for (let i = 1; i < welcomeLines.length; i++) {
      drawText(welcomeLines[i], { size: 9, color: c.GRAY })
    }
    y -= 10

    // Vision quote box (if guide data)
    const gr = lead.formData?.guideResponses || {}
    if (gr.visionViaje) {
      const vTitle = `Familia ${familyName}, me contaron que lo que mas quieren de este viaje es:`
      const vQuote = `"${gr.visionViaje}"`

      const vTitleLines = Math.max(1, Math.ceil(fontWidth(vTitle, helveticaBold, 9) / (CONTENT_W - 30)))
      const vQuoteLines = Math.max(1, Math.ceil(fontWidth(vQuote, helveticaOblique, 9) / (CONTENT_W - 30)))
      const vBoxH = (vTitleLines + vQuoteLines) * 13 + 18

      ensureSpace(vBoxH + 20)
      const boxTop = y
      page.drawRectangle({
        x: MARGIN_L, y: boxTop - vBoxH,
        width: CONTENT_W, height: vBoxH,
        color: c.LIGHT_BG, borderColor: c.PURPLE, borderWidth: 1,
      })
      y -= 8
      drawText(vTitle, { size: 9, font: helveticaBold, color: c.PURPLE, indent: 10 })
      y -= 2
      drawText(vQuote, { size: 9, font: helveticaOblique, color: c.DARK, indent: 10, maxWidth: CONTENT_W - 20 })
      y = boxTop - vBoxH - 10

      drawText('Tengan eso en mente cada dia \u2014 este itinerario esta disenado para que lo logren.', {
        size: 10, font: helveticaBold, color: c.DARK,
      })
    }
    y -= 10
  }

  // ── Vertical spacer ──

  function skip(amount) { y -= amount }
  function getY() { return y }
  function setY(val) { y = val }
  function getPage() { return page }
  function getPageNum() { return pageNum }

  return {
    newPage, ensureSpace, drawText, drawTextCentered, drawRichText,
    drawHeading1, drawHeading2, drawHeading3,
    drawBox, drawTable, drawBullet, drawCheckbox, drawCover,
    skip, getY, setY, getPage, getPageNum,
    fonts: { helvetica, helveticaBold, helveticaOblique, helveticaBoldOblique, titleFont },
  }
}

// ── LAZY LOADERS ─────────────────────────────────────────────────────────

async function loadPdfLib() {
  const { PDFDocument, rgb: _rgb, StandardFonts } = await import('pdf-lib')
  const fontkit = (await import('@pdf-lib/fontkit')).default
  initColors(_rgb)
  return { PDFDocument, rgb: _rgb, StandardFonts, fontkit }
}

async function loadMammoth() {
  try {
    return await import('mammoth')
  } catch {
    console.warn('[PDF Finalizer] mammoth not available')
    return null
  }
}

async function setupDoc(PDFDocument, _rgb, StandardFonts, fontkit) {
  const doc = await PDFDocument.create()
  doc.registerFontkit(fontkit)

  const helvetica = await doc.embedFont(StandardFonts.Helvetica)
  const helveticaBold = await doc.embedFont(StandardFonts.HelveticaBold)
  const helveticaOblique = await doc.embedFont(StandardFonts.HelveticaOblique)
  const helveticaBoldOblique = await doc.embedFont(StandardFonts.HelveticaBoldOblique)

  // Try custom title font
  let titleFont = helveticaBold
  try {
    const resp = await fetch('/guides/PlayfairDisplay-Bold.ttf')
    if (resp.ok) {
      const fontBytes = await resp.arrayBuffer()
      titleFont = await doc.embedFont(fontBytes)
    }
  } catch {
    console.log('[PDF Finalizer] Playfair not found, using Helvetica Bold')
  }

  // Try logo
  let logoImage = null
  try {
    const resp = await fetch('/guides/logo-mind2magic.jpg')
    if (resp.ok) {
      const logoBytes = await resp.arrayBuffer()
      logoImage = await doc.embedJpg(logoBytes)
    }
  } catch {
    console.log('[PDF Finalizer] Logo not found')
  }

  return {
    doc,
    fonts: { helvetica, helveticaBold, helveticaOblique, helveticaBoldOblique, titleFont },
    logoImage,
  }
}

// ── FIND LEAD HELPER ─────────────────────────────────────────────────────

function findLead(leadId) {
  return store.leads.find(l => l.id === leadId) || store.historico?.find(l => l.id === leadId)
}

// ═══════════════════════════════════════════════════════════════════════════
// MAIN EXPORT: generateFinalPdf
// ═══════════════════════════════════════════════════════════════════════════

export async function generateFinalPdf(leadId) {
  const lead = findLead(leadId)
  if (!lead) { window.toast?.('Lead no encontrado', 'error'); return }

  const versions = lead.formData?._itineraryVersions || []
  const editedVersion = [...versions].reverse().find(v => v.type === 'edited')
  const generatedVersion = [...versions].reverse().find(v => v.type === 'generated')
  const sourceVersion = editedVersion || generatedVersion

  if (!sourceVersion) {
    window.toast?.('No hay itinerario generado. Genera el DOCX primero.', 'error')
    return
  }

  window.toast?.('Generando PDF final...', 'success')

  try {
    const { PDFDocument, rgb: _rgb, StandardFonts, fontkit } = await loadPdfLib()
    const { doc, fonts, logoImage } = await setupDoc(PDFDocument, _rgb, StandardFonts, fontkit)
    const R = createRenderer(doc, fonts, logoImage)

    // Determine rendering mode
    const rawHtml = editedVersion?.rawHtml || editedVersion?.html || null

    console.log('[PDF] Mode:', rawHtml ? 'HTML (from edited DOCX)' : 'DIRECT (from template)')
    console.log('[PDF] editedVersion exists:', !!editedVersion, '| rawHtml length:', rawHtml?.length || 0)

    if (rawHtml) {
      // ── HTML MODE: Render from Sara's edited DOCX content ──
      await renderFromHtml(R, rawHtml, lead, sourceVersion.config || generatedVersion?.config)
    } else {
      // ── DIRECT MODE: Render from lead data + content-blocks ──
      console.warn('[PDF] No edited DOCX HTML found — falling back to template-based PDF')
      const config = sourceVersion.config || generatedVersion?.config || {}
      await renderDirect(R, lead, config)
    }

    // Save and download
    const pdfBytes = await doc.save()
    const familyName = extractFamilyName(lead)
    const filename = `Itinerario Familia ${familyName} - Mind2Magic.pdf`
    const blob = new Blob([pdfBytes], { type: 'application/pdf' })
    downloadBlob(blob, filename)

    // Auto-upload to dashboard if exists
    if (leadId && window.autoUploadToDashboard) {
      window.autoUploadToDashboard(leadId, blob, filename, 'Itinerario Personalizado')
    }

    // Store final_pdf version
    if (!lead.formData) lead.formData = {}
    if (!lead.formData._itineraryVersions) lead.formData._itineraryVersions = []
    lead.formData._itineraryVersions.push({
      version: lead.formData._itineraryVersions.length + 1,
      type: 'final_pdf',
      createdAt: new Date().toISOString(),
      pages: R.getPageNum(),
    })

    window.syncLeadToSupabase?.(lead)
    window.renderAll?.()
    window.toast?.(`PDF generado: ${filename} (${R.getPageNum()} paginas)`, 'success')

  } catch (err) {
    console.error('[PDF Finalizer] Error:', err)
    window.toast?.(`Error generando PDF: ${err.message}`, 'error')
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// DOWNLOAD FINAL PDF (re-download from stored version)
// ═══════════════════════════════════════════════════════════════════════════

export async function downloadFinalPdf(leadId) {
  // For now, regenerate the PDF (we don't store the blob)
  await generateFinalPdf(leadId)
}

// ═══════════════════════════════════════════════════════════════════════════
// HTML MODE: Parse mammoth HTML and render as PDF
// ═══════════════════════════════════════════════════════════════════════════

async function renderFromHtml(R, htmlStr, lead, config) {
  // Draw cover page
  R.drawCover(lead, config)

  // Parse HTML into DOM
  const parser = new DOMParser()
  const htmlDoc = parser.parseFromString(htmlStr, 'text/html')

  // Track current section context for park coloring
  let currentParkCode = null

  // Start content on a new page
  R.newPage()

  // Walk top-level elements
  const body = htmlDoc.body
  for (const node of body.childNodes) {
    processHtmlNode(R, node, { currentParkCode, lead, config }, (newPark) => {
      currentParkCode = newPark
    })
  }
}

function processHtmlNode(R, node, ctx, setParkCode) {
  if (node.nodeType === Node.TEXT_NODE) {
    const text = node.textContent.trim()
    if (!text) return

    // Check for section markers
    const markerMatch = text.match(/\{\{M2M_SECTION:([^}]+)\}\}/)
    if (markerMatch) {
      const sectionId = markerMatch[1]
      const parkMatch = sectionId.match(/PARK_(?:DAY|RIDES|RESTAURANTS|SHOWS|CHARACTERS):(\w+)/)
      if (parkMatch) setParkCode(parkMatch[1])
      return // Don't render the marker text
    }

    // Render visible text
    if (text.includes('\u2610') || text.includes('\u25A1') || text.includes('[ ]')) {
      renderCheckboxText(R, text, ctx)
    } else {
      R.drawText(text)
    }
    return
  }

  if (node.nodeType !== Node.ELEMENT_NODE) return

  const tag = node.tagName.toLowerCase()
  const textContent = node.textContent?.trim() || ''

  // Check for section markers in text content
  const markerMatch = textContent.match(/\{\{M2M_SECTION:([^}]+)\}\}/)
  if (markerMatch) {
    const sectionId = markerMatch[1]
    const parkMatch = sectionId.match(/PARK_(?:DAY|RIDES|RESTAURANTS|SHOWS|CHARACTERS):(\w+)/)
    if (parkMatch) setParkCode(parkMatch[1])
    // If this element ONLY contains a marker, skip it
    const withoutMarker = textContent.replace(/\{\{M2M_SECTION:[^}]+\}\}/g, '').trim()
    if (!withoutMarker) return
  }

  // Strip markers from display text
  const displayText = textContent.replace(/\{\{M2M_SECTION:[^}]+\}\}/g, '').trim()
  if (!displayText && !['table', 'ul', 'ol', 'br', 'hr'].includes(tag)) return

  switch (tag) {
    case 'h1':
      renderH1(R, displayText, ctx)
      break

    case 'h2':
      R.drawHeading2(displayText)
      break

    case 'h3':
      R.drawHeading3(displayText)
      break

    case 'p':
      renderParagraph(R, node, ctx)
      break

    case 'table':
      renderTable(R, node, ctx)
      break

    case 'ul':
    case 'ol':
      renderList(R, node, ctx)
      break

    case 'br':
      R.skip(6)
      break

    case 'hr':
      R.skip(8)
      R.ensureSpace(10)
      R.getPage().drawLine({
        start: { x: MARGIN_L, y: R.getY() },
        end: { x: PAGE_W - MARGIN_R, y: R.getY() },
        thickness: 0.5, color: c.GRAY,
      })
      R.skip(8)
      break

    case 'div':
    case 'section':
    case 'article':
    case 'span':
      // Recurse into children
      for (const child of node.childNodes) {
        processHtmlNode(R, child, ctx, setParkCode)
      }
      break

    default:
      // Fallback: treat as paragraph
      if (displayText) R.drawText(displayText)
      break
  }
}

function renderH1(R, text, ctx) {
  // Check if this is a park day heading
  const parkCode = detectParkCodeFromText(text) || ctx.currentParkCode
  const color = parkCode ? (PARK_COLORS[parkCode] || c.PURPLE) : c.PURPLE
  R.drawHeading1(text, color)
}

function detectParkCodeFromText(text) {
  const lower = text.toLowerCase()
  if (lower.includes('magic kingdom')) return 'mk'
  if (lower.includes('hollywood studios')) return 'hs'
  if (lower.includes('epcot')) return 'ep'
  if (lower.includes('animal kingdom')) return 'ak'
  if (lower.includes('universal studios')) return 'uso'
  if (lower.includes('islands of adventure')) return 'ioa'
  if (lower.includes('epic universe')) return 'epic'
  return null
}

function detectParkCodeFromSectionId(sectionId) {
  const match = sectionId.match(/(?:parkDay|parkRides|parkRestaurants|parkShows|parkCharacters)_(\w+)/)
  if (match) return match[1]
  // Also handle the colon format from SECTION_MARKERS
  const colonMatch = sectionId.match(/PARK_(?:DAY|RIDES|RESTAURANTS|SHOWS|CHARACTERS):(\w+)/)
  if (colonMatch) return colonMatch[1]
  return null
}

function renderParagraph(R, node, ctx) {
  const runs = []
  extractInlineRuns(node, runs, R.fonts)

  if (!runs.length) return

  // Check for checkbox content
  const fullText = runs.map(r => r.text).join('')
  if (fullText.includes('\u2610') || fullText.includes('\u25A1') || fullText.includes('[ ]')) {
    renderCheckboxText(R, fullText, ctx)
    return
  }

  // If all runs share same style, use simple drawText
  const allSameFont = runs.every(r => r.font === runs[0].font && r.size === runs[0].size && r.color === runs[0].color)
  if (allSameFont) {
    R.drawText(fullText.replace(/\{\{M2M_SECTION:[^}]+\}\}/g, ''), {
      font: runs[0].font, size: runs[0].size, color: runs[0].color,
    })
  } else {
    // Strip markers from run text
    for (const run of runs) {
      run.text = run.text.replace(/\{\{M2M_SECTION:[^}]+\}\}/g, '')
    }
    R.drawRichText(runs.filter(r => r.text.trim()))
  }
}

function extractInlineRuns(node, runs, fonts) {
  for (const child of node.childNodes) {
    if (child.nodeType === Node.TEXT_NODE) {
      const text = child.textContent
      if (text.trim()) runs.push({ text, font: fonts.helvetica, size: 10, color: c.DARK })
      continue
    }
    if (child.nodeType !== Node.ELEMENT_NODE) continue

    const tag = child.tagName.toLowerCase()
    if (tag === 'strong' || tag === 'b') {
      const innerRuns = []
      extractInlineRuns(child, innerRuns, fonts)
      for (const r of innerRuns) {
        r.font = r.font === fonts.helveticaOblique ? fonts.helveticaBoldOblique : fonts.helveticaBold
      }
      runs.push(...innerRuns)
    } else if (tag === 'em' || tag === 'i') {
      const innerRuns = []
      extractInlineRuns(child, innerRuns, fonts)
      for (const r of innerRuns) {
        r.font = r.font === fonts.helveticaBold ? fonts.helveticaBoldOblique : fonts.helveticaOblique
      }
      runs.push(...innerRuns)
    } else if (tag === 'br') {
      // Line break — skip
    } else {
      // Recurse into other inline elements
      extractInlineRuns(child, runs, fonts)
    }
  }
}

function renderCheckboxText(R, text, ctx) {
  // Parse checkbox line: "[ ] Ride Name — Sara comment" or "[ ] * Ride Name"
  const clean = text
    .replace(/\u2610/g, '[ ]')
    .replace(/\u25A1/g, '[ ]')
    .replace(/\{\{M2M_SECTION:[^}]+\}\}/g, '')
    .trim()

  if (!clean) return

  const isSelected = clean.includes('[ ] *') || clean.includes('[x]')
  let name = clean.replace(/\[ *[x ]?\] *\*? */i, '').trim()

  // Split name and comment
  let comment = ''
  const dashIdx = name.indexOf(' \u2014 ')
  if (dashIdx > 0) {
    comment = name.substring(dashIdx + 3).trim()
    name = name.substring(0, dashIdx).trim()
  } else {
    const simpleDash = name.indexOf(' - ')
    if (simpleDash > 20) { // Only split on dash if name is long enough
      comment = name.substring(simpleDash + 3).trim()
      name = name.substring(0, simpleDash).trim()
    }
  }

  R.drawCheckbox(name, comment, { isBold: isSelected })
}

function renderTable(R, tableNode, ctx) {
  const rows = []
  const trs = tableNode.querySelectorAll('tr')
  for (const tr of trs) {
    const cells = []
    for (const td of tr.querySelectorAll('td, th')) {
      cells.push(td.textContent?.replace(/\{\{M2M_SECTION:[^}]+\}\}/g, '').trim() || '')
    }
    if (cells.length) rows.push(cells)
  }

  if (!rows.length) return

  // Determine header color from park context
  const headerColor = ctx.currentParkCode ? (PARK_COLORS[ctx.currentParkCode] || c.PURPLE) : c.PURPLE
  const altBg = ctx.currentParkCode ? (PARK_LIGHT[ctx.currentParkCode] || c.LIGHT_BG) : c.LIGHT_BG

  // Calculate column widths based on number of columns
  const numCols = rows[0].length
  let colWidths
  if (numCols === 3) {
    colWidths = [CONTENT_W * 0.2, CONTENT_W * 0.15, CONTENT_W * 0.65]
  } else if (numCols === 4) {
    colWidths = [CONTENT_W * 0.2, CONTENT_W * 0.15, CONTENT_W * 0.12, CONTENT_W * 0.53]
  } else {
    colWidths = rows[0].map(() => CONTENT_W / numCols)
  }

  R.drawTable(rows, { headerColor, colWidths, altBg })
}

function renderList(R, listNode, ctx) {
  const items = listNode.querySelectorAll('li')
  for (const li of items) {
    const text = li.textContent?.replace(/\{\{M2M_SECTION:[^}]+\}\}/g, '').trim() || ''
    if (text) R.drawBullet(text)
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// DIRECT MODE: Generate PDF from lead data (no DOCX)
// ═══════════════════════════════════════════════════════════════════════════

async function renderDirect(R, lead, config) {
  const familyName = extractFamilyName(lead)
  const parks = config.parks || detectParks(lead)
  const hotelInfo = config.hotelInfo || classifyHotel(lead)
  const gr = lead.formData?.guideResponses || {}
  const hasGuideData = Object.keys(gr).length > 1
  const closuresData = config.closures || {}
  const parkSchedule = config.parkSchedule || buildDefaultSchedule(lead, parks)
  const viajeros = lead.formData?.grupoViajeros || lead.grupoViajeros || []
  const noCoasters = detectNoCoasters(gr)
  const diningPlanType = config.diningPlanType || detectMealPlanType(lead)
  const hotelName = getFirstHotel(lead)

  const isDis = parks.some(p => ['mk', 'hs', 'ep', 'ak'].includes(p))
  const isUni = parks.some(p => ['uso', 'ioa', 'epic'].includes(p))

  // ── COVER PAGE ──
  R.drawCover(lead, config)

  // ── AGENDA TABLE ──
  R.newPage()
  R.drawHeading2('AGENDA DEL VIAJE')
  R.skip(5)

  const agendaRows = buildAgendaRows(lead, parkSchedule, hotelName)
  if (agendaRows.length > 1) {
    R.drawTable(agendaRows, { colWidths: [CONTENT_W * 0.2, CONTENT_W * 0.15, CONTENT_W * 0.65] })
  }
  R.skip(5)

  // ── DATOS IMPORTANTES (Lightning Lane) ──
  if (isDis) {
    renderLightningLane(R, parks, hotelInfo)
  }

  // ── DISNEY SPRINGS ──
  if (isDis) {
    renderDisneySprings(R)
  }

  // ── CITYWALK ──
  if (isUni) {
    renderCityWalk(R)
  }

  // ── CHECK-IN DAY ──
  renderCheckInDay(R, lead, hotelInfo, hotelName)

  // ── PARK DAYS ──
  for (const day of parkSchedule) {
    const parkCode = day.parkCode
    if (!parkCode) continue

    const parkName = getParkName(parkCode)
    const parkClosures = closuresData[parkCode] || []
    const color = PARK_COLORS[parkCode] || c.PURPLE

    // Park day header banner
    const dateLabel = day.dateLabel || formatDateShort(day.date)
    R.drawHeading1(`${dateLabel} \u2014 ${parkName}`, color)
    R.skip(5)

    // Hours
    if (day.hours) {
      R.drawText(`Horario: ${day.hours}`, { font: R.fonts.helveticaBold, size: 10 })
      R.skip(3)
    }

    // Hotel benefit boxes
    renderHotelBenefitBox(R, parkCode, hotelInfo, hotelName)

    // LL tip per park (Disney only)
    const parkData = PARK_RIDES[parkCode]
    if (parkData?.llTip) {
      R.drawText(parkData.llTip, { font: R.fonts.helveticaBold, size: 9 })
      R.skip(3)
    }

    // Arrival tip
    if (parkData?.arrivalTip) {
      R.drawText(parkData.arrivalTip, { font: R.fonts.helveticaOblique, size: 9, color: c.GRAY })
      R.skip(8)
    }

    // ── RIDES ──
    const isDisneyPark = ['mk', 'hs', 'ep', 'ak'].includes(parkCode)
    if (isDisneyPark && parkData) {
      renderDisneyParkRides(R, parkCode, parkData, gr, hasGuideData, noCoasters, parkClosures)
    } else if (['uso', 'ioa'].includes(parkCode)) {
      renderUniversalParkRides(R, parkCode, gr, hasGuideData, noCoasters, parkClosures)
    } else if (parkCode === 'epic') {
      renderEpicUniverse(R, gr, hasGuideData, noCoasters, parkClosures)
    }

    // ── SHOWS ──
    const showsData = PARK_SHOWS[parkCode]
    if (showsData?.length) {
      const filteredShows = filterShows(showsData, gr[`shows_${parkCode}`] || [], hasGuideData, gr.finalDia)
      if (filteredShows.length) {
        R.drawHeading3('Shows y Entretenimiento')
        for (const show of filteredShows) {
          if (isClosedAttraction(show.name, parkClosures)) continue
          R.drawBullet(`${show.name}${show.times ? ` (${show.times})` : ''}${show.sara ? ` \u2014 ${show.sara}` : ''}`)
        }
        R.skip(5)
      }
    }

    // ── ENTERTAINMENT SCHEDULE ──
    // Prefer date-specific entertainment from Disney Finder API
    const dateEntPdf = config?.entertainment?.[day.date]?.[parkCode]  // per-date format
    const flatEntPdf = config?.entertainment?.[parkCode]  // flat format (legacy/live)
    const liveEntPdf = config?.entertainmentLive?.[parkCode]  // ThemeParks.wiki live fallback
    const liveEntertainmentPdf = dateEntPdf || flatEntPdf || liveEntPdf
    const staticEntertainmentPdf = PARK_ENTERTAINMENT[parkCode]
    const useLivePdf = liveEntertainmentPdf?.length > 0
    let entertainmentItemsPdf

    if (useLivePdf) {
      const TYPE_ORDER_PDF = { parade: 0, show: 1, fireworks: 2, character: 3 }
      const sorted = [...liveEntertainmentPdf].sort((a, b) =>
        (TYPE_ORDER_PDF[a.type] ?? 1) - (TYPE_ORDER_PDF[b.type] ?? 1)
      )
      entertainmentItemsPdf = sorted.map(live => {
        const timesStr = live.type === 'character'
          ? (live.hours || '')
          : (live.times || []).join(', ')
        let saraComment = ''
        if (staticEntertainmentPdf?.length) {
          const liveNameLower = live.name.toLowerCase()
          const match = staticEntertainmentPdf.find(s => {
            const sn = s.name.toLowerCase()
            return sn.includes(liveNameLower) || liveNameLower.includes(sn)
          })
          if (match?.sara) saraComment = match.sara
        }
        return { name: live.name, location: '', times: timesStr, sara: saraComment }
      })
    } else {
      entertainmentItemsPdf = staticEntertainmentPdf
    }

    if (entertainmentItemsPdf?.length) {
      R.drawHeading3('CONOCER PERSONAJES Y EVENTOS DURANTE EL DIA')
      R.drawText(PARK_ENTERTAINMENT.disclaimer, { font: R.fonts.helveticaOblique, size: 7, color: c.GRAY })
      R.skip(3)
      const PARK_COLORS_PDF = { mk: [21/255, 101/255, 192/255], hs: [198/255, 40/255, 40/255], ep: [106/255, 27/255, 154/255], ak: [46/255, 125/255, 50/255] }
      const headerRgb = PARK_COLORS_PDF[parkCode] || [0.2, 0.2, 0.2]
      const entRows = [['Entretenimiento', 'Horario(s)']]
      for (const item of entertainmentItemsPdf) {
        let nameText
        if (item.location && item.sara) {
          nameText = `${item.name} (${item.location}) \u2014 ${item.sara}`
        } else if (item.location) {
          nameText = `${item.name} (${item.location})`
        } else if (item.sara) {
          nameText = `${item.name} \u2014 ${item.sara}`
        } else {
          nameText = item.name
        }
        entRows.push([nameText, item.times])
      }
      R.drawTable(entRows, { headerColor: rgb(...headerRgb), colWidths: [CONTENT_W * 0.65, CONTENT_W * 0.35] })
      R.skip(8)
    }

    // ── RESTAURANTS ──
    const restaurants = PARK_RESTAURANTS[parkCode]
    if (restaurants?.length) {
      renderRestaurantTable(R, parkCode, restaurants, diningPlanType)
    }

    // ── CLOSURES SUMMARY ──
    if (parkClosures.length) {
      R.skip(5)
      R.drawText('Atracciones en mantenimiento al momento de generar este itinerario:', {
        font: R.fonts.helveticaBold, size: 9, color: rgb(230 / 255, 81 / 255, 0),
      })
      for (const cl of parkClosures) {
        R.drawBullet(`${cl.name} (${cl.entity_type === 'SHOW' ? 'Show' : 'Atraccion'})`, {
          size: 8, color: c.GRAY, font: R.fonts.helveticaOblique,
        })
      }
      R.drawText('Nota: Las atracciones en mantenimiento pueden cambiar. Verifica en la app.', {
        font: R.fonts.helveticaOblique, size: 8, color: c.GRAY,
      })
      R.skip(5)
    }
  }

  // ── MEAL PLAN ──
  if (isDis && diningPlanType && diningPlanType !== 'none') {
    R.newPage()
    renderMealPlan(R, diningPlanType)
  }

  // ── SHOPPING ──
  renderShopping(R)

  // ── INFO ADICIONAL ──
  renderInfoAdicional(R)
}

// ═══════════════════════════════════════════════════════════════════════════
// DIRECT MODE SUB-RENDERERS
// ═══════════════════════════════════════════════════════════════════════════

function buildAgendaRows(lead, schedule, hotelName) {
  const rows = [['Fecha', 'Dia', 'Actividad']]
  const startDate = getTravelStartDate(lead)

  // Check-in day
  if (startDate) {
    rows.push([formatDateShort(startDate), 'Dia 1', `Llegada + Check-in ${hotelName}`])
  }

  // Park days
  for (let i = 0; i < schedule.length; i++) {
    const day = schedule[i]
    const dateLabel = day.dateLabel || formatDateShort(day.date) || ''
    const parkName = getParkName(day.parkCode)
    rows.push([dateLabel, `Dia ${i + 2}`, `${parkName}`])
  }

  // Check-out day
  if (schedule.length > 0) {
    const lastDate = schedule[schedule.length - 1].date
    if (lastDate) {
      const d = new Date(lastDate)
      d.setDate(d.getDate() + 1)
      rows.push([formatDateShort(d), `Dia ${schedule.length + 2}`, 'Check-out + Vuelo'])
    }
  }

  return rows
}

function renderLightningLane(R, parks, hotelInfo) {
  R.drawHeading2(LIGHTNING_LANE.title)
  R.skip(3)

  R.drawText(LIGHTNING_LANE.intro, { size: 9, color: c.GRAY })
  R.skip(8)

  // Multi Pass section
  R.drawText(LIGHTNING_LANE.multipass.title, { font: R.fonts.helveticaBold, size: 10 })
  R.skip(2)
  R.drawText(LIGHTNING_LANE.multipass.priceRange, { size: 9 })
  for (const pp of LIGHTNING_LANE.multipass.perPark) {
    if (parks.some(p => pp.park.toLowerCase().includes(getParkName(p).toLowerCase().split(' ')[0]))) {
      R.drawText(`${pp.park}: ${pp.price}`, { size: 9, indent: 15, color: c.GRAY })
    }
  }
  R.skip(5)
  R.drawText(LIGHTNING_LANE.multipass.buyAdvance, { size: 9 })
  R.skip(5)

  // LL Groups tables (paired parks side by side)
  const disneyParks = parks.filter(p => ['mk', 'hs', 'ep', 'ak'].includes(p))
  for (const pk of disneyParks) {
    const data = PARK_RIDES[pk]
    if (!data?.llGroups) continue
    const groups = data.llGroups
    const color = PARK_COLORS[pk] || c.PURPLE

    R.ensureSpace(30)

    // Header
    const headerText = `En ${getParkName(pk)}`
    const halfW = CONTENT_W
    R.getPage().drawRectangle({
      x: MARGIN_L, y: R.getY() - 4,
      width: halfW, height: 20,
      color,
    })
    R.getPage().drawText(stripEmoji(headerText), {
      x: MARGIN_L + 8, y: R.getY() + 1,
      size: 9, font: R.fonts.helveticaBold, color: c.WHITE,
    })
    R.skip(24)

    // Groups
    if (groups.group1) {
      R.drawText(groups.group1.label, { font: R.fonts.helveticaBold, size: 8, indent: 5 })
      for (const ride of groups.group1.rides) {
        R.drawBullet(ride, { size: 8, indent: 10 })
      }
      R.skip(3)
    }
    if (groups.group2) {
      R.drawText(groups.group2.label, { font: R.fonts.helveticaBold, size: 8, indent: 5 })
      for (const ride of groups.group2.rides) {
        R.drawBullet(ride, { size: 8, indent: 10 })
      }
      R.skip(3)
    }

    // Single Pass
    if (groups.singlePass?.length) {
      R.skip(3)
      for (const sp of groups.singlePass) {
        R.drawText(`${sp.name} ${sp.price}`, { size: 8, indent: 10, color: c.GRAY })
      }
    }
    R.skip(8)
  }

  // Credit card warning box
  R.drawBox([`${LIGHTNING_LANE.creditCardWarningPrefix} ${LIGHTNING_LANE.creditCardWarningText}`], {
    bgColor: c.LIGHT_RED,
    borderColor: rgb(200 / 255, 50 / 255, 50 / 255),
    textColor: rgb(0.6, 0, 0),
    textFont: R.fonts.helveticaBold,
    textSize: 8,
  })
  R.skip(5)

  // Single Pass
  R.drawText(LIGHTNING_LANE.singlePass.title, { font: R.fonts.helveticaBold, size: 10 })
  R.drawText(LIGHTNING_LANE.singlePass.buyAdvance, { font: R.fonts.helveticaBold, size: 9 })
  R.skip(3)
  for (const rule of LIGHTNING_LANE.singlePass.rules) {
    R.drawBullet(rule, { size: 9, indent: 10 })
  }
  R.skip(5)
  R.drawText('Listado de filas individuales pagas en cada parque:', { font: R.fonts.helveticaBold, size: 9 })
  for (const sp of LIGHTNING_LANE.singlePass.perPark) {
    R.drawText(`${sp.park}: ${sp.items.replace(/\n\s+/g, ' / ')}`, { size: 9, indent: 5 })
  }
  R.skip(8)

  // Multi pass explanation
  R.drawText(LIGHTNING_LANE.multiPassExplanation, { size: 9, color: c.GRAY })
  R.skip(5)

  // Reminder
  const reminder = hotelInfo.isDisneyGuest ? LIGHTNING_LANE.reminderDisney : LIGHTNING_LANE.reminderNonDisney
  R.drawText(reminder, { font: R.fonts.helveticaBold, size: 9 })
  R.skip(10)
}

function renderDisneySprings(R) {
  R.drawHeading2(DISNEY_SPRINGS.title)
  R.drawText(DISNEY_SPRINGS.intro, { size: 10, color: c.GRAY })
  R.skip(5)

  for (const cat of DISNEY_SPRINGS.categories || []) {
    R.drawText(cat.name, { font: R.fonts.helveticaBold, size: 10 })
    for (const rest of cat.restaurants) {
      const sara = rest.tip || rest.sara || ''
      const name = rest.name || ''
      const type = rest.type || ''
      R.drawBullet(`${name} (${type})${sara ? ` \u2014 ${sara}` : ''}`, { size: 9, indent: 10 })
    }
    R.skip(5)
  }
  R.skip(5)
}

function renderCityWalk(R) {
  if (!CITY_WALK?.title) return
  R.drawHeading2(CITY_WALK.title)
  if (CITY_WALK.intro) R.drawText(CITY_WALK.intro, { size: 10, color: c.GRAY })
  R.skip(5)

  for (const rest of CITY_WALK.restaurants || []) {
    R.drawBullet(`${rest.name} (${rest.type})${rest.sara ? ` \u2014 ${rest.sara}` : ''}`, { size: 9, indent: 10 })
  }
  R.skip(10)
}

function renderCheckInDay(R, lead, hotelInfo, hotelName) {
  R.drawHeading2('Dia de Llegada \u2014 Check-in')
  R.drawText(`Hoy es dia de llegada. El check-in en ${hotelName} es a las 3:00 PM (a veces dejan hacer check-in temprano si hay disponibilidad, pregunten).`, { size: 10 })
  R.skip(8)

  R.drawText('Si llegan temprano:', { font: R.fonts.helveticaBold, size: 10 })
  R.drawBullet('Dejen las maletas en el hotel (bell services las guarda gratis)', { size: 9, indent: 10 })
  R.drawBullet('Vayan a Disney Springs o CityWalk a almorzar y explorar', { size: 9, indent: 10 })
  R.drawBullet('Descarguen la app My Disney Experience / Universal Orlando y configuren todo', { size: 9, indent: 10 })
  R.skip(10)

  // Hotel benefits box
  if (hotelInfo.isDisneyGuest || hotelInfo.isUniversalGuest) {
    const benefits = []
    benefits.push(`Beneficios de su hotel (${hotelName}):`)
    if (hotelInfo.isDisneyGuest) {
      benefits.push('+ Early Entry (30 min antes de la apertura) en TODOS los parques Disney')
      benefits.push('+ Transporte gratis Disney (buses, monoriel, Skyliner, boats)')
      benefits.push('+ MagicBand+ funcional desde el check-in')
      if (hotelInfo.disneyTier === 'deluxe') {
        benefits.push('+ Extended Evening Hours en parques seleccionados')
      }
    }
    if (hotelInfo.isUniversalGuest) {
      benefits.push('+ Early Park Admission (1 hora antes) en parques Universal')
      if (hotelInfo.universalTier === 'premier') {
        benefits.push('+ Express Pass INCLUIDO para todos los integrantes del grupo')
      }
    }
    R.drawBox(benefits, { bgColor: c.LIGHT_BG, borderColor: c.PURPLE, textFont: R.fonts.helveticaBold, textSize: 9, textColor: c.DARK })
    R.skip(5)
  }

  // Celebrations
  R.drawHeading3('Celebraciones Especiales')
  R.drawText('En Disney pueden pedir un boton de celebracion GRATIS en Guest Relations. Los personajes y empleados los felicitan todo el dia \u2014 vale mucho la pena.', { size: 9 })
  R.skip(10)
}

function renderHotelBenefitBox(R, parkCode, hotelInfo, hotelName) {
  const isDPark = ['mk', 'hs', 'ep', 'ak'].includes(parkCode)
  const isUPark = ['uso', 'ioa', 'epic'].includes(parkCode)

  if (isDPark && hotelInfo.isDisneyGuest) {
    const bgColor = PARK_LIGHT[parkCode] || c.LIGHT_BG
    const borderColor = PARK_COLORS[parkCode] || c.PURPLE
    const textColor = PARK_TEXT[parkCode] || c.DARK

    const lines = [`[HOTEL] Ustedes tienen Early Entry por hospedarse en ${hotelName}. Entran 30 minutos antes que el publico general. USENLO \u2014 es la mejor ventaja para hacer las atracciones populares con menos fila.`]
    if (hotelInfo.disneyTier === 'deluxe') {
      lines.push(`[DELUXE] Por hospedarse en ${hotelName} (Deluxe), tienen Extended Evening Hours. Pueden quedarse hasta mas tarde en parques seleccionados.`)
    }
    R.drawBox(lines, { bgColor, borderColor, textColor, textFont: R.fonts.helveticaBold, textSize: 8 })
  } else if (isUPark && hotelInfo.isUniversalGuest) {
    const bgColor = PARK_LIGHT[parkCode] || c.LIGHT_BG
    const borderColor = PARK_COLORS[parkCode] || c.PURPLE
    const textColor = PARK_TEXT[parkCode] || c.DARK

    const lines = [`[HOTEL] Ustedes tienen Early Park Admission por hospedarse en ${hotelName}. Entran 1 hora antes que el publico general.`]
    if (hotelInfo.universalTier === 'premier') {
      lines.push(`[PREMIER] Por hospedarse en ${hotelName} (Premier), tienen Express Pass INCLUIDO. No necesitan comprar fila rapida!`)
    }
    R.drawBox(lines, { bgColor, borderColor, textColor, textFont: R.fonts.helveticaBold, textSize: 8 })
  } else if (isDPark && !hotelInfo.isDisneyGuest) {
    R.drawText('No se hospedan en hotel Disney, asi que entran con el horario regular. Lleguen lo mas temprano posible.', {
      font: R.fonts.helveticaOblique, size: 9, color: c.GRAY,
    })
    R.skip(5)
  }
  R.skip(5)
}

function renderDisneyParkRides(R, parkCode, parkData, gr, hasGuideData, noCoasters, parkClosures) {
  R.drawHeading3('ORDEN IDEAL DEL PARQUE')

  const selectedRides = gr[`atracciones_${parkCode}`] || []
  const ritmo = gr.ritmo || 'normal'
  const allRides = [...(parkData.rideOrder || [])]
  const ifTimePermits = [...(parkData.ifTimePermits || [])]

  // Filter out closed rides
  const openRides = allRides.filter(r => !isClosedAttraction(r.name, parkClosures))
  const openIfTime = ifTimePermits.filter(r => !isClosedAttraction(r.name, parkClosures))

  // Filter out coasters if requested
  let ridesToRender = openRides
  let ifTimeToRender = openIfTime
  const removedCoasters = []

  if (noCoasters) {
    removedCoasters.push(...openRides.filter(r => r.isCoaster))
    removedCoasters.push(...openIfTime.filter(r => r.isCoaster))
    ridesToRender = openRides.filter(r => !r.isCoaster)
    ifTimeToRender = openIfTime.filter(r => !r.isCoaster)
  }

  const { primary, secondary, ifTimePermitsFiltered } = filterRides(
    ridesToRender, selectedRides, hasGuideData, ritmo, ifTimeToRender
  )

  // Primary rides
  for (const ride of primary) {
    R.drawCheckbox(ride.name, ride.sara || '', { isBold: !!ride._selected })
  }

  // "Si les queda tiempo" secondary rides
  if (secondary.length || (ifTimePermitsFiltered || []).length) {
    R.skip(5)
    R.drawText('Si les queda tiempo:', { font: R.fonts.helveticaBold, size: 10 })
    for (const ride of secondary) {
      R.drawCheckbox(ride.name, ride.sara || '', { isBold: !!ride._selected })
    }
    for (const ride of (ifTimePermitsFiltered || [])) {
      R.drawCheckbox(ride.name, ride.sara || '', { isBold: !!ride._selected })
    }
  }

  // Coaster disclaimer
  if (noCoasters && removedCoasters.length) {
    R.skip(8)
    renderCoasterDisclaimer(R, removedCoasters, parkClosures)
  }
  R.skip(8)
}

function renderUniversalParkRides(R, parkCode, gr, hasGuideData, noCoasters, parkClosures) {
  const parkData = UNIVERSAL_PARKS?.[parkCode]
  if (!parkData) return

  R.drawHeading3('ORDEN IDEAL DEL PARQUE')

  const topRides = (parkData.topRides || []).filter(r => !isClosedAttraction(r.name, parkClosures))
  const removedCoasters = []

  let ridesToRender = topRides
  if (noCoasters) {
    removedCoasters.push(...topRides.filter(r => r.isCoaster))
    ridesToRender = topRides.filter(r => !r.isCoaster)
  }

  for (const ride of ridesToRender) {
    const selected = (gr[`atracciones_${parkCode}`] || []).some(s =>
      s.toLowerCase().includes(ride.name.toLowerCase().substring(0, 10))
    )
    R.drawCheckbox(ride.name, ride.sara || '', { isBold: selected })
  }

  if (noCoasters && removedCoasters.length) {
    R.skip(8)
    renderCoasterDisclaimer(R, removedCoasters, parkClosures)
  }
  R.skip(8)
}

function renderEpicUniverse(R, gr, hasGuideData, noCoasters, parkClosures) {
  if (!EPIC_UNIVERSE?.zones?.length) return

  R.drawHeading3('ORDEN IDEAL DEL PARQUE')

  for (const zone of EPIC_UNIVERSE.zones) {
    R.drawText(zone.name || zone.zone || '', { font: R.fonts.helveticaBold, size: 10 })
    R.skip(3)

    const rides = (zone.rides || []).filter(r => !isClosedAttraction(r.name, parkClosures))
    let ridesToShow = rides
    if (noCoasters) {
      ridesToShow = rides.filter(r => !r.isCoaster)
    }

    for (const ride of ridesToShow) {
      R.drawCheckbox(ride.name, ride.sara || '', { isBold: false })
    }

    // Shows in the zone
    if (zone.shows?.length) {
      for (const show of zone.shows) {
        R.drawBullet(`${show.name}${show.sara ? ` \u2014 ${show.sara}` : ''}`, { size: 9 })
      }
    }

    // Restaurants in the zone
    if (zone.restaurants?.length) {
      for (const rest of zone.restaurants) {
        R.drawBullet(`${rest.name} (${rest.type || ''})${rest.sara ? ` \u2014 ${rest.sara}` : ''}`, { size: 9, color: c.GRAY })
      }
    }
    R.skip(5)
  }
  R.skip(5)
}

function renderCoasterDisclaimer(R, coasters, parkClosures) {
  R.drawText('Si cambian de opinion...', { font: R.fonts.helveticaBold, size: 10 })
  R.drawText('Como nos mencionaron que prefieren no montanas rusas, las sacamos del itinerario. Pero por si cambian de opinion estando alla, estas son las opciones:', {
    font: R.fonts.helveticaOblique, size: 9, color: c.GRAY,
  })
  R.skip(3)

  const suaves = coasters.filter(r => r.coasterType === 'suave' && !isClosedAttraction(r.name, parkClosures))
  const intensas = coasters.filter(r => r.coasterType !== 'suave' && !isClosedAttraction(r.name, parkClosures))

  if (suaves.length) {
    R.drawText('Montanas rusas suaves (aptas para toda la familia):', {
      font: R.fonts.helveticaBold, size: 9, color: rgb(46 / 255, 125 / 255, 50 / 255),
    })
    for (const ride of suaves) {
      R.drawBullet(`${ride.name}${ride.sara ? ` \u2014 ${ride.sara}` : ''}`, { size: 9 })
    }
    R.skip(3)
  }

  if (intensas.length) {
    R.drawText('Mas intensas (no las recomiendo para este viaje):', {
      font: R.fonts.helveticaBold, size: 9, color: rgb(204 / 255, 0, 0),
    })
    for (const ride of intensas) {
      R.drawBullet(ride.name, { size: 8, color: c.GRAY, font: R.fonts.helveticaOblique })
    }
  }
}

function renderRestaurantTable(R, parkCode, restaurants, diningPlanType) {
  R.drawHeading3('Restaurantes Recomendados')

  // Filter by dining plan type
  let filtered = restaurants
  if (diningPlanType === 'quick_service') {
    // Prioritize quick service, still show table service as options
    filtered = [...restaurants.filter(r => r.service === 'quick'), ...restaurants.filter(r => r.service !== 'quick')]
  }

  const headerColor = PARK_COLORS[parkCode] || c.PURPLE
  const altBg = PARK_LIGHT[parkCode] || c.LIGHT_BG

  const rows = [['Restaurante', 'Ubicacion', 'Tipo', 'Comentario']]
  for (const rest of filtered) {
    rows.push([rest.name, rest.location || '', rest.type || '', rest.sara || ''])
  }

  R.drawTable(rows, {
    headerColor, altBg,
    colWidths: [CONTENT_W * 0.22, CONTENT_W * 0.17, CONTENT_W * 0.13, CONTENT_W * 0.48],
  })
}

function renderMealPlan(R, planType) {
  R.drawHeading2(MEAL_PLAN.title)
  R.drawText(MEAL_PLAN.intro, { size: 10 })
  R.skip(5)

  const plan = MEAL_PLAN.types[planType] || MEAL_PLAN.types.quick_service
  R.drawText(plan.name, { font: R.fonts.helveticaBold, size: 10 })
  R.drawText(plan.perDay, { size: 9 })
  R.skip(3)
  R.drawText(plan.tip, { font: R.fonts.helveticaOblique, size: 9 })
  R.skip(5)
  R.drawText(MEAL_PLAN.snackTip, { font: R.fonts.helveticaOblique, size: 9, color: c.GRAY })
  R.skip(10)
}

function renderShopping(R) {
  if (!SHOPPING?.title) return
  R.ensureSpace(100)
  R.drawHeading2(SHOPPING.title)
  if (SHOPPING.intro) R.drawText(SHOPPING.intro, { size: 10 })
  R.skip(5)

  for (const outlet of SHOPPING.outlets || []) {
    R.drawText(outlet.name, { font: R.fonts.helveticaBold, size: 10 })
    if (outlet.address) R.drawText(outlet.address, { size: 9 })
    if (outlet.tip) R.drawText(outlet.tip, { font: R.fonts.helveticaOblique, size: 9, color: c.GRAY })
    R.skip(5)
  }

  if (SHOPPING.shoppingTips?.length) {
    R.drawText('Tips de compras:', { font: R.fonts.helveticaBold, size: 9 })
    for (const tip of SHOPPING.shoppingTips) {
      R.drawBullet(tip, { size: 9 })
    }
  }
  R.skip(10)
}

function renderInfoAdicional(R) {
  R.newPage()
  R.drawHeading2(INFO_ADICIONAL.title)

  // Contacts
  if (INFO_ADICIONAL.contacts) {
    R.drawText(INFO_ADICIONAL.contacts.title, { font: R.fonts.helveticaBold, size: 10 })
    for (const contact of INFO_ADICIONAL.contacts.items || []) {
      R.drawBullet(`${contact.name} \u2014 ${contact.role} (${contact.phone})`, { size: 9 })
    }
    if (INFO_ADICIONAL.contacts.note) {
      R.drawText(INFO_ADICIONAL.contacts.note, { font: R.fonts.helveticaOblique, size: 9, color: c.GRAY })
    }
    R.skip(8)
  }

  // Orlando activities (categorized lists)
  if (INFO_ADICIONAL.orlandoActivities) {
    R.drawText(INFO_ADICIONAL.orlandoActivities.title, { font: R.fonts.helveticaBold, size: 10 })
    for (const cat of INFO_ADICIONAL.orlandoActivities.categories || []) {
      R.drawText(cat.name, { font: R.fonts.helveticaBold, size: 9, indent: 5 })
      for (const item of cat.items || []) {
        R.drawBullet(item, { size: 9, indent: 15 })
      }
      R.skip(3)
    }
    R.skip(8)
  }

  // Essential tips
  if (INFO_ADICIONAL.essentialTips) {
    R.drawText(INFO_ADICIONAL.essentialTips.title, { font: R.fonts.helveticaBold, size: 10 })
    for (const tip of INFO_ADICIONAL.essentialTips.items || []) {
      R.drawBullet(tip, { size: 9 })
    }
    R.skip(8)
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// DIRECT PDF — Shortcut entry point (no DOCX, no versions needed)
// ═══════════════════════════════════════════════════════════════════════════

export async function generateDirectPdf(leadId) {
  const lead = findLead(leadId)
  if (!lead) { window.toast?.('Lead no encontrado', 'error'); return }

  // Check if there's an edited version — if so, use generateFinalPdf instead
  const versions = lead.formData?._itineraryVersions || []
  const editedVersion = [...versions].reverse().find(v => v.type === 'edited')
  if (editedVersion?.rawHtml) {
    console.log('[PDF] Edited DOCX found — redirecting to generateFinalPdf')
    return generateFinalPdf(leadId)
  }

  // Use latest generated config or build default
  const generatedVersion = [...versions].reverse().find(v => v.type === 'generated')
  const config = generatedVersion?.config || {
    parks: detectParks(lead),
    hotelInfo: classifyHotel(lead),
    diningPlanType: detectMealPlanType(lead),
    parkSchedule: buildDefaultSchedule(lead, detectParks(lead)),
    closures: {},
  }

  window.toast?.('Generando PDF directo...', 'success')

  try {
    const { PDFDocument, rgb: _rgb, StandardFonts, fontkit } = await loadPdfLib()
    const { doc, fonts, logoImage } = await setupDoc(PDFDocument, _rgb, StandardFonts, fontkit)
    const R = createRenderer(doc, fonts, logoImage)

    await renderDirect(R, lead, config)

    const pdfBytes = await doc.save()
    const familyName = extractFamilyName(lead)
    const filename = `Itinerario Familia ${familyName} - Mind2Magic.pdf`
    const blob = new Blob([pdfBytes], { type: 'application/pdf' })
    downloadBlob(blob, filename)

    // Auto-upload to dashboard if exists
    if (leadId && window.autoUploadToDashboard) {
      window.autoUploadToDashboard(leadId, blob, filename, 'Itinerario Personalizado')
    }

    // Save version
    if (!lead.formData) lead.formData = {}
    if (!lead.formData._itineraryVersions) lead.formData._itineraryVersions = []
    lead.formData._itineraryVersions.push({
      version: lead.formData._itineraryVersions.length + 1,
      type: 'final_pdf',
      createdAt: new Date().toISOString(),
      pages: R.getPageNum(),
      source: 'direct',
    })

    window.syncLeadToSupabase?.(lead)
    window.renderAll?.()
    window.toast?.(`PDF generado: ${filename} (${R.getPageNum()} paginas)`, 'success')

  } catch (err) {
    console.error('[PDF Finalizer] Direct PDF error:', err)
    window.toast?.(`Error generando PDF: ${err.message}`, 'error')
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// WINDOW BRIDGE
// ═══════════════════════════════════════════════════════════════════════════

Object.assign(window, {
  generateFinalPdf,
  generateFinalPdfFromVersion: generateFinalPdf,
  downloadFinalPdf,
  generateDirectPdf,
})
